/* =========================================================================
   Cigar Golf — Theme JS
   Vanilla custom elements + small modules. Keeps deps to zero.
   ========================================================================= */

(function () {
  'use strict';

  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  const $ = (sel, root) => (root || document).querySelector(sel);

  const formatMoney = (cents) => {
    const format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    const value = cents / 100;
    const withDelims = (num, precision, thousands, decimal) => {
      const parts = num.toFixed(precision).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    let formatted;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': formatted = withDelims(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator': formatted = withDelims(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': formatted = withDelims(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': formatted = withDelims(value, 2, "'", '.'); break;
      default: formatted = withDelims(value, 2, ',', '.');
    }
    return format.replace(placeholder, formatted);
  };

  /* -----------------------------------------------------------------------
   * Drawer helpers
   * --------------------------------------------------------------------- */
  const drawerHideTimers = new WeakMap();
  function openDrawer(el) {
    if (!el) return;
    const pending = drawerHideTimers.get(el);
    if (pending) {
      clearTimeout(pending);
      drawerHideTimers.delete(el);
    }
    el.hidden = false;
    requestAnimationFrame(() => {
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
    });
    document.body.style.overflow = 'hidden';
    trapFocus(el);
  }

  function closeDrawer(el) {
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    const pending = drawerHideTimers.get(el);
    if (pending) clearTimeout(pending);
    drawerHideTimers.set(el, setTimeout(() => {
      el.hidden = true;
      drawerHideTimers.delete(el);
    }, 250));
    releaseFocus();
    syncDrawerToggles(el);
  }

  function syncDrawerToggles(el) {
    let toggles = [];
    if (el && el.matches('[data-mobile-nav]')) toggles = $$('[data-mobile-nav-open]');
    else if (el && el.matches('[data-search-drawer]')) toggles = $$('[data-search-toggle]');
    toggles.forEach(btn => btn.setAttribute('aria-expanded', 'false'));
  }

  let focusReturn = null;
  let trappedContainer = null;
  const onTrapKeydown = (e) => {
    if (e.key !== 'Tab' || !trappedContainer) return;
    const focusable = Array.from(trappedContainer.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(node => node.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && (document.activeElement === first || !trappedContainer.contains(document.activeElement))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  function trapFocus(container) {
    focusReturn = document.activeElement;
    trappedContainer = container;
    document.addEventListener('keydown', onTrapKeydown);
    const focusable = container.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable.length) focusable[0].focus();
  }
  function releaseFocus() {
    trappedContainer = null;
    document.removeEventListener('keydown', onTrapKeydown);
    if (focusReturn && typeof focusReturn.focus === 'function') focusReturn.focus();
    focusReturn = null;
  }

  /* -----------------------------------------------------------------------
   * Mobile nav
   * --------------------------------------------------------------------- */
  const mobileNav = $('[data-mobile-nav]');
  $$('[data-mobile-nav-open]').forEach(btn => {
    btn.addEventListener('click', () => {
      btn.setAttribute('aria-expanded', 'true');
      openDrawer(mobileNav);
    });
  });
  $$('[data-mobile-nav-close]').forEach(btn => {
    btn.addEventListener('click', () => {
      const opener = $('[data-mobile-nav-open]');
      if (opener) opener.setAttribute('aria-expanded', 'false');
      closeDrawer(mobileNav);
    });
  });
  $$('[data-mobile-nav-expand]').forEach(btn => {
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      const item = btn.closest('.mobile-nav__item');
      const sublist = item && item.querySelector('.mobile-nav__sublist');
      if (sublist) sublist.hidden = expanded;
    });
  });

  /* -----------------------------------------------------------------------
   * Search drawer
   * --------------------------------------------------------------------- */
  const searchDrawer = $('[data-search-drawer]');
  $$('[data-search-toggle]').forEach(btn => {
    btn.addEventListener('click', () => {
      btn.setAttribute('aria-expanded', 'true');
      openDrawer(searchDrawer);
      const input = searchDrawer && searchDrawer.querySelector('input[name="q"]');
      if (input) setTimeout(() => input.focus(), 60);
    });
  });
  $$('[data-search-close]').forEach(btn => {
    btn.addEventListener('click', () => closeDrawer(searchDrawer));
  });

  /* -----------------------------------------------------------------------
   * Predictive search dropdown
   * --------------------------------------------------------------------- */
  const searchSuggest = (function initPredictiveSearch() {
    const input = $('[data-search-input]');
    const results = $('[data-search-results]');
    if (!input || !results) return;

    const escapeHtml = (s) => String(s).replace(/[&<>"']/g, c => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

    const show = () => {
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };
    const hide = () => {
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
    };

    const renderEmpty = (q) => {
      results.innerHTML = `<p class="search-suggest__empty">No results for "${escapeHtml(q)}"</p>`;
      show();
    };

    // NOTE: suggest.json's `price` is rendered as-is on purpose. Its unit
    // (cents vs. currency units) is not documented, and guessing wrong
    // renders a price that is off by 100x — worse than an unstyled one.
    // The correct fix is the section-rendering variant of the endpoint
    // (/search/suggest?section_id=...), where Liquid's money filter formats
    // it server-side. Escaped here since it lands in innerHTML.
    const formatSuggestPrice = (price) => (price == null ? '' : escapeHtml(price));

    const renderResults = (products, q) => {
      const list = products.map(p => `
        <li>
          <a class="search-suggest__item" href="${p.url}">
            <span class="search-suggest__media">
              ${p.image ? `<img src="${p.image}" alt="" loading="lazy" width="64" height="80">` : ''}
            </span>
            <span class="search-suggest__body">
              <span class="search-suggest__title">${escapeHtml(p.title)}</span>
              <span class="search-suggest__price">${formatSuggestPrice(p.price)}</span>
            </span>
          </a>
        </li>
      `).join('');
      const viewAll = `${window.Shopify.routes.root}search?q=${encodeURIComponent(q)}&type=product`;
      results.innerHTML = `
        <ul class="search-suggest__list">${list}</ul>
        <a class="search-suggest__viewall" href="${viewAll}">View all results</a>
      `;
      show();
    };

    let abortCtrl = null;
    async function fetchSuggestions(q) {
      if (abortCtrl) abortCtrl.abort();
      abortCtrl = new AbortController();
      try {
        const url = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`;
        const res = await fetch(url, {
          headers: { 'Accept': 'application/json' },
          signal: abortCtrl.signal,
        });
        if (!res.ok) return;
        const data = await res.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        if (products.length === 0) renderEmpty(q);
        else renderResults(products, q);
      } catch (e) {
        if (e.name !== 'AbortError') {
          // Silent fail; user can still submit the form to search results page.
        }
      }
    }

    let timer;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) {
        hide();
        results.innerHTML = '';
        return;
      }
      timer = setTimeout(() => fetchSuggestions(q), 180);
    });

    return { clear: () => { hide(); results.innerHTML = ''; } };
  })();

  /* -----------------------------------------------------------------------
   * Customer addresses: edit-toggle + delete confirmation
   * --------------------------------------------------------------------- */
  $$('[data-address-edit]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.addressEdit;
      const target = document.getElementById('address-edit-' + id);
      if (target) target.open = !target.open;
    });
  });
  $$('[data-address-delete]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.addressDelete;
      const msg = btn.dataset.addressConfirm || 'Are you sure?';
      if (!window.confirm(msg)) return;
      fetch(`${window.Shopify.routes.root}account/addresses/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
          'X-Shopify-Topic': 'customer/address/delete',
          'X-Shopify-Authenticated': 'true',
        },
        body: '_method=delete',
      }).then(() => window.location.reload());
    });
  });

  /* -----------------------------------------------------------------------
   * Shop the Look — hotspots + popovers
   * --------------------------------------------------------------------- */
  const shopTheLook = (function initShopTheLook() {
    const sections = $$('[data-shop-the-look]');
    if (sections.length === 0) return null;

    const closeAll = () => {
      sections.forEach(s => {
        $$('[data-hotspot]', s).forEach(h => {
          const id = h.getAttribute('aria-controls');
          const popover = id ? document.getElementById(id) : null;
          h.setAttribute('aria-expanded', 'false');
          if (popover) popover.hidden = true;
        });
      });
    };

    const positionPopover = (popover) => {
      const x = parseFloat(popover.dataset.x);
      popover.classList.remove('is-left');
      if (x >= 50) popover.classList.add('is-left');
    };

    sections.forEach(section => {
      const hotspots = $$('[data-hotspot]', section);

      hotspots.forEach(hotspot => {
        hotspot.addEventListener('click', (e) => {
          e.stopPropagation();
          const id = hotspot.getAttribute('aria-controls');
          const popover = id ? document.getElementById(id) : null;
          if (!popover) return;
          const wasOpen = hotspot.getAttribute('aria-expanded') === 'true';
          closeAll();
          if (!wasOpen) {
            positionPopover(popover);
            popover.hidden = false;
            hotspot.setAttribute('aria-expanded', 'true');
          }
        });
      });

      $$('[data-hotspot-close]', section).forEach(btn => {
        btn.addEventListener('click', () => closeAll());
      });
    });

    document.addEventListener('click', (e) => {
      const inSection = sections.some(s => s.contains(e.target));
      if (!inSection) closeAll();
    });

    return { closeAll };
  })();

  /* -----------------------------------------------------------------------
   * Collection filters drawer (mobile only — desktop is permanent sidebar)
   * --------------------------------------------------------------------- */
  const collectionFilters = (function initCollectionFilters() {
    const filters = $('[data-collection-filters]');
    if (!filters) return null;
    const openBtn = $('[data-filters-open]');
    const desktop = window.matchMedia('(min-width: 990px)');

    const syncAria = () => {
      if (desktop.matches) {
        // Rotating/resizing to desktop while the mobile drawer is open must
        // release the scroll lock — the drawer becomes a permanent sidebar.
        if (filters.classList.contains('is-open')) {
          filters.classList.remove('is-open');
          if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
          document.body.style.overflow = '';
        }
        filters.removeAttribute('aria-hidden');
        filters.removeAttribute('inert');
      } else {
        const isOpen = filters.classList.contains('is-open');
        filters.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        // `inert` removes the element from the accessibility tree AND tab order,
        // which prevents focusable filter inputs from being reachable while the
        // drawer is closed on mobile. Fixes the Lighthouse aria-hidden-focus audit.
        if (isOpen) {
          filters.removeAttribute('inert');
        } else {
          filters.setAttribute('inert', '');
        }
      }
    };

    const open = () => {
      filters.classList.add('is-open');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      if (!desktop.matches) document.body.style.overflow = 'hidden';
      syncAria();
    };
    const close = () => {
      filters.classList.remove('is-open');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      syncAria();
    };

    if (openBtn) openBtn.addEventListener('click', open);
    $$('[data-filters-close]', filters).forEach(el => el.addEventListener('click', close));
    desktop.addEventListener('change', syncAria);
    syncAria();

    return { filters, close };
  })();

  /* -----------------------------------------------------------------------
   * Cart drawer
   * --------------------------------------------------------------------- */
  const cartDrawer = $('#cart-drawer');
  const cartTypeIsDrawer = document.body.getAttribute('data-cart-type') !== 'page';

  function openCartDrawer() { openDrawer(cartDrawer); }

  // Cart link opens drawer (unless cart_type is page)
  $$('[data-cart-link]').forEach(link => {
    link.addEventListener('click', (e) => {
      if (!cartDrawer || !cartTypeIsDrawer) return;
      // Allow open in new tab.
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      refreshCartDrawer().then(openCartDrawer);
    });
  });
  $$('[data-cart-drawer-close]').forEach(btn => {
    btn.addEventListener('click', () => closeDrawer(cartDrawer));
  });

  async function fetchCart() {
    const res = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { 'Accept': 'application/json' } });
    return res.json();
  }

  async function fetchCartDrawerHtml() {
    const url = `${window.Shopify.routes.root}?section_id=cart-drawer-fragment`;
    const res = await fetch(url, { headers: { Accept: 'text/html' } });
    if (res.ok) return res.text();
    return null;
  }

  async function refreshCartDrawer() {
    if (!cartDrawer) return;
    try {
      const cart = await fetchCart();
      updateCartCount(cart.item_count);
      const body = $('[data-cart-drawer-body]', cartDrawer);
      // Fallback: fetch HTML if a dedicated section route is wired up,
      // otherwise re-fetch on next navigation. We keep this graceful.
      const html = await fetchCartDrawerHtml().catch(() => null);
      if (html && body) {
        const frag = new DOMParser().parseFromString(html, 'text/html');
        const newBody = frag.querySelector('[data-cart-drawer-body]');
        if (newBody) body.innerHTML = newBody.innerHTML;
      }
    } catch (e) {
      // Silent — drawer will still show stale state until next page load.
    }
  }

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.hidden = count === 0;
      el.setAttribute('aria-label', `${count} ${count === 1 ? 'item' : 'items'} in cart`);
    });
  }

  /* -----------------------------------------------------------------------
   * Quantity controls
   * --------------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const minus = e.target.closest('[data-qty-minus]');
    const plus = e.target.closest('[data-qty-plus]');
    if (!minus && !plus) return;
    const wrap = (minus || plus).closest('[data-qty-control]');
    const input = wrap && wrap.querySelector('input[type="number"]');
    if (!input) return;
    const min = parseInt(input.getAttribute('min') || '0', 10);
    let value = parseInt(input.value || '0', 10) || 0;
    value += minus ? -1 : 1;
    if (value < min) value = min;
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* -----------------------------------------------------------------------
   * Cart line updates (drawer + cart page)
   * --------------------------------------------------------------------- */
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-quantity]');
    if (!input) return;
    const raw = input.value.trim();
    const quantity = parseInt(raw, 10);
    // A cleared or garbled field is not a removal request — restore the last
    // server-rendered quantity instead of silently deleting the line.
    if (raw === '' || Number.isNaN(quantity) || quantity < 0) {
      input.value = input.defaultValue || '1';
      return;
    }
    updateLine(input.dataset.lineKey, quantity);
  });

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cart-remove]');
    if (!btn) return;
    e.preventDefault();
    updateLine(btn.dataset.lineKey, 0);
  });

  async function updateLine(key, quantity) {
    try {
      const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const data = await res.json();
      // A 4xx here returns an error payload, not a cart — re-fetch the real
      // cart so the badge and drawer re-sync instead of rendering "undefined".
      const failed = !res.ok || (data.status && data.status >= 400);
      const cart = failed ? await fetchCart() : data;
      if (typeof cart.item_count === 'number') updateCartCount(cart.item_count);

      if (window.location.pathname.endsWith('/cart')) {
        window.location.reload();
      } else if (cartDrawer && cartDrawer.classList.contains('is-open')) {
        refreshCartDrawer();
      }
    } catch (err) {
      console.warn('Cart update failed', err);
    }
  }

  /* -----------------------------------------------------------------------
   * Variant picker (custom element)
   * --------------------------------------------------------------------- */
  class VariantRadios extends HTMLElement {
    constructor() {
      super();
      this.variants = [];
      this.optionNames = [];
      try {
        const data = this.querySelector('[data-variant-data]');
        if (data) this.variants = JSON.parse(data.textContent);
      } catch (e) {}
      try {
        const opts = this.querySelector('[data-option-names]');
        if (opts) this.optionNames = JSON.parse(opts.textContent);
      } catch (e) {}
      this.addEventListener('change', this.onChange.bind(this));
      this.updateAvailability();
    }

    get options() {
      return $$('fieldset.variant-picker__option', this).map(fs => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onChange(e) {
      // Track which option triggered the change so we can pivot the other
      // selections around it when the resulting combo has no matching SKU.
      const changedInput = e && e.target && e.target.matches('input[data-option-position]') ? e.target : null;
      const changedIdx = changedInput
        ? parseInt(changedInput.dataset.optionPosition, 10) - 1
        : null;

      let selected = this.options;
      let matched = this.variants.find(v =>
        v.options.every((opt, idx) => opt === selected[idx])
      );

      // If the just-changed selection combined with the others has no SKU,
      // keep the user's new pick locked and auto-switch other options to the
      // first combination that actually exists (preferring available stock).
      if (!matched && changedIdx != null) {
        const lockedValue = selected[changedIdx];
        const pool = this.variants.filter(v => v.options[changedIdx] === lockedValue);
        const fallback = pool.find(v => v.available) || pool[0];
        if (fallback) {
          $$('fieldset.variant-picker__option', this).forEach((fs, idx) => {
            if (idx === changedIdx) return;
            const target = fallback.options[idx];
            $$('input[type="radio"]', fs).forEach(r => { r.checked = (r.value === target); });
          });
          selected = this.options;
          matched = this.variants.find(v =>
            v.options.every((opt, i) => opt === selected[i])
          );
        }
      }

      // Update selected option label
      $$('fieldset.variant-picker__option', this).forEach((fs) => {
        const checked = fs.querySelector('input:checked');
        const display = fs.querySelector('[data-selected-value]');
        if (checked && display) display.textContent = checked.value;
      });

      if (!matched) {
        // No matching SKU and pivot couldn't recover. Don't re-run updateAvailability
        // here — it would mark the user's just-clicked value as data-no-sku and hide
        // it, making their selection visually vanish. Leave existing flags alone and
        // just disable the ATC button.
        this.toggleSubmit(false, true);
        return;
      }

      // Update hidden input
      const idInput = document.querySelector('[data-selected-variant-id]');
      if (idInput) idInput.value = matched.id;

      // Update URL
      if (this.dataset.updateUrl === 'true' && history.replaceState) {
        const url = new URL(window.location);
        url.searchParams.set('variant', matched.id);
        history.replaceState({}, '', url);
      }

      // Update price + atc
      this.updatePrice(matched);
      this.toggleSubmit(matched.available, !matched.available);
      this.updateAvailability();
      this.updateGallery(matched);
    }

    updatePrice(variant) {
      document.querySelectorAll('[data-product-price]').forEach((priceWrap) => {
        const price = priceWrap.querySelector('.price__regular');
        const compare = priceWrap.querySelector('.price__compare');
        const priceEl = priceWrap.querySelector('.price');
        if (price) price.textContent = formatMoney(variant.price);
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          if (compare) {
            compare.textContent = formatMoney(variant.compare_at_price);
            compare.style.display = '';
          } else if (priceEl) {
            const s = document.createElement('s');
            s.className = 'price__compare';
            s.textContent = formatMoney(variant.compare_at_price);
            priceEl.appendChild(s);
          }
          if (priceEl) priceEl.classList.add('price--on-sale');
        } else {
          if (compare) compare.style.display = 'none';
          if (priceEl) priceEl.classList.remove('price--on-sale');
        }
      });
    }

    toggleSubmit(available, soldOut) {
      const btn = document.querySelector('[data-atc-button]');
      const label = document.querySelector('[data-atc-label]');
      const stickyBtn = document.querySelector('[data-sticky-atc-button]');
      const strings = (window.theme && window.theme.strings) || {};
      let text;
      if (available) text = strings.addToCart || 'Add to cart';
      else if (soldOut) text = strings.soldOut || 'Sold out';
      else text = strings.unavailable || 'Unavailable';

      if (btn) btn.disabled = !available;
      if (label) label.textContent = text;
      if (stickyBtn) {
        stickyBtn.disabled = !available;
        stickyBtn.textContent = text;
      }
      // Keep the "Sold out" note under the price in sync with the selection.
      document.querySelectorAll('[data-availability-note]').forEach(note => {
        note.hidden = !soldOut;
      });
    }

    updateAvailability() {
      // For every option, mark each value with two signals:
      //   data-no-sku       = no variant in the product has this value at any position
      //                       (hide the swatch entirely — orphan value)
      //   data-unavailable  = a variant with this value exists, but it doesn't combine
      //                       with the OTHER current selections OR is sold out
      //                       (show as crossed-out / disabled, but still clickable so
      //                        auto-pivot in onChange() can move the user to a valid combo)
      //
      // SYMMETRIC FILTER: each option is filtered by every OTHER selected option,
      // regardless of position. This previously filtered by earlier-position options
      // only, which made the first option effectively never cross out — it could only
      // go unavailable if the value was sold out in every single combination. On a
      // product whose first option is Size (The Nineteenth Tee is Size × Color, while
      // most of the catalogue is Color × Size), that meant sold-out sizes never showed
      // as sold out, while colors did. Filtering both directions makes the picker read
      // the same way no matter which order the options were created in.
      //
      // Crossed-out values stay clickable on purpose: the input is not disabled, so
      // onChange() auto-pivots the other axes to a valid variant. Nothing is a dead end.
      const selected = this.options;
      $$('input[data-option-position]', this).forEach(input => {
        const pos = parseInt(input.dataset.optionPosition, 10) - 1;
        const value = input.value;

        // Does any variant at all have this option value?
        const existsAnywhere = this.variants.some(v => v.options[pos] === value);
        if (!existsAnywhere) {
          input.setAttribute('data-no-sku', 'true');
          input.removeAttribute('data-unavailable');
          return;
        }
        input.removeAttribute('data-no-sku');

        // Every other selected option constrains this value — not just earlier ones.
        const combo = this.variants.filter(v => {
          if (v.options[pos] !== value) return false;
          for (let i = 0; i < selected.length; i++) {
            if (i === pos) continue;
            if (selected[i] != null && selected[i] !== v.options[i]) return false;
          }
          return true;
        });
        if (combo.length > 0 && combo.some(v => v.available)) {
          input.removeAttribute('data-unavailable');
        } else {
          input.setAttribute('data-unavailable', 'true');
        }
      });
    }

    updateGallery(matched) {
      const gallery = document.querySelector('[data-product-gallery]');
      if (!gallery) return;
      this.filterGalleryByColor(matched, gallery);
      this.activateVariantMedia(matched, gallery);
    }

    filterGalleryByColor(matched, gallery) {
      const colorIndices = this.optionNames
        .map((name, idx) => /colou?r/i.test(name) ? idx : -1)
        .filter(idx => idx >= 0);

      const figures = Array.from(gallery.querySelectorAll('.product-gallery__item'));
      const thumbs = Array.from(gallery.querySelectorAll('.product-gallery__thumb'));
      const dots = Array.from(gallery.querySelectorAll('.product-gallery__dot'));

      if (colorIndices.length === 0) {
        figures.forEach(f => f.classList.remove('is-hidden-by-filter'));
        thumbs.forEach(t => t.classList.remove('is-hidden-by-filter'));
        dots.forEach(d => d.classList.remove('is-hidden-by-filter'));
        return;
      }

      // Tokenize each selected color value into words, preserving order across the option
      // dimensions (so "White / White" → ['white', 'white'], "Sand Grey / Blue" → ['sand', 'grey', 'blue']).
      // The matching rule then becomes "alt contains these tokens in this order" instead of
      // "alt contains each token somewhere", which fixes false positives like a Grey hat
      // matching the White filter because its alt mentions "white logo".
      const STOPWORDS = new Set(['with', 'and', 'the', 'a', 'of']);
      const tokens = colorIndices
        .map(idx => matched.options[idx])
        .filter(Boolean)
        .flatMap(value =>
          value.toLowerCase().split(/[^a-z0-9]+/).filter(w => w && !STOPWORDS.has(w))
        );

      let orderRegex = null;
      if (tokens.length > 0) {
        const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = tokens.map(t => `\\b${escape(t)}\\b`).join('.*?');
        orderRegex = new RegExp(pattern, 'i');
      }

      const visibleIds = new Set();
      figures.forEach(fig => {
        const img = fig.querySelector('img');
        const alt = ((img && img.getAttribute('alt')) || '').toLowerCase().trim();
        // Blank alt = universal image (e.g. lifestyle shot) → always show.
        const matches = !alt || (orderRegex ? orderRegex.test(alt) : true);
        fig.classList.toggle('is-hidden-by-filter', !matches);
        if (matches) visibleIds.add(fig.dataset.mediaId);
      });

      // Fallback: if the filter would hide every image (e.g. alts don't follow the
      // "Color1 ... Color2 ..." convention yet), show everything rather than going blank.
      if (visibleIds.size === 0) {
        figures.forEach(f => {
          f.classList.remove('is-hidden-by-filter');
          visibleIds.add(f.dataset.mediaId);
        });
      }

      thumbs.forEach(t => t.classList.toggle('is-hidden-by-filter', !visibleIds.has(t.dataset.thumbId)));
      dots.forEach(d => d.classList.toggle('is-hidden-by-filter', !visibleIds.has(d.dataset.thumbId)));
    }

    activateVariantMedia(matched, gallery) {
      const targetId = matched.featured_media_id ? String(matched.featured_media_id) : null;
      const figures = Array.from(gallery.querySelectorAll('.product-gallery__item'));

      // Prefer the variant's featured media; fall back to the first non-hidden figure
      // (handles variants with no assigned media, or media hidden by the color filter).
      let target = targetId ? figures.find(f => f.dataset.mediaId === targetId) : null;
      if (!target || target.classList.contains('is-hidden-by-filter')) {
        target = figures.find(f => !f.classList.contains('is-hidden-by-filter')) || figures[0];
      }
      if (!target) return;

      const activeId = target.dataset.mediaId;
      figures.forEach(f => {
        f.classList.toggle('is-active', f.dataset.mediaId === activeId);
      });
      gallery.querySelectorAll('.product-gallery__thumb, .product-gallery__dot').forEach(b => {
        b.classList.toggle('is-active', b.dataset.thumbId === activeId);
      });

      // Mobile gallery is a horizontal scroller — bring the new active item into view.
      const viewport = gallery.querySelector('[data-gallery-viewport]');
      if (viewport && window.matchMedia('(max-width: 749px)').matches) {
        viewport.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      }
    }
  }
  if (!customElements.get('variant-radios')) {
    customElements.define('variant-radios', VariantRadios);
  }

  /* -----------------------------------------------------------------------
   * Hero carousel (multi-slide hero with autoplay, swipe, keyboard nav)
   * --------------------------------------------------------------------- */
  class HeroCarousel extends HTMLElement {
    constructor() {
      super();
      this.slides = $$('[data-hero-slide]', this);
      this.dots = $$('[data-hero-dot]', this);
      this.prevBtn = this.querySelector('[data-hero-prev]');
      this.nextBtn = this.querySelector('[data-hero-next]');
      this.index = 0;
      this.autoplayMs = (parseInt(this.dataset.autoplay || '0', 10) || 0) * 1000;
      this.timer = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.wired = false;
      this.onVisibility = () => {
        if (document.hidden) this.stop();
        else this.start();
      };
    }

    connectedCallback() {
      if (this.slides.length <= 1) return;

      // Re-connects (theme editor moves, DOM re-parenting) must not stack a
      // second set of control listeners on the same elements.
      document.addEventListener('visibilitychange', this.onVisibility);
      if (this.wired) {
        this.start();
        return;
      }
      this.wired = true;

      this.dots.forEach(dot => {
        dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.heroDot, 10)));
      });
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this.index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this.index + 1));

      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.start();
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.index - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + 1); }
      });

      let startX = null;
      this.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) this.goTo(this.index + (dx < 0 ? 1 : -1));
        startX = null;
      }, { passive: true });

      this.start();
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.stop();
    }

    goTo(n) {
      const len = this.slides.length;
      if (!len) return;
      this.index = ((n % len) + len) % len;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      this.dots.forEach((dot, i) => {
        const active = i === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });
      // If autoplay was running, restart its interval so the user gets the full duration on the new slide.
      if (this.timer) this.start();
    }

    start() {
      if (this.reducedMotion) return;
      if (!this.autoplayMs || this.slides.length <= 1) return;
      this.stop();
      this.timer = setInterval(() => this.goTo(this.index + 1), this.autoplayMs);
    }

    stop() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }
  }
  if (!customElements.get('hero-carousel')) {
    customElements.define('hero-carousel', HeroCarousel);
  }

  /* -----------------------------------------------------------------------
   * Add-to-cart
   * --------------------------------------------------------------------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form.product__form');
    if (!form) return;
    e.preventDefault();

    const btn = form.querySelector('[data-atc-button]');
    const feedback = form.querySelector('[data-atc-feedback]');
    const wrap = form.querySelector('[data-buy-buttons]');
    if (btn) btn.disabled = true;
    if (wrap) wrap.classList.add('is-loading');

    try {
      const formData = new FormData(form);
      formData.append('sections', 'cart-drawer-fragment');
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData
      });
      const data = await res.json();
      if (data.status && data.status >= 400) {
        throw new Error(data.description || data.message || 'Cart error');
      }

      const cart = await fetchCart();
      updateCartCount(cart.item_count);

      if (feedback) {
        feedback.textContent = 'Added to cart';
        feedback.dataset.status = 'success';
        feedback.hidden = false;
      }

      if (cartDrawer && cartTypeIsDrawer) {
        await refreshCartDrawer();
        openCartDrawer();
      } else {
        window.location.href = window.theme.cartUrl;
      }
    } catch (err) {
      if (feedback) {
        feedback.textContent = err.message || 'Unable to add to cart';
        feedback.dataset.status = 'error';
        feedback.hidden = false;
      }
    } finally {
      if (btn) btn.disabled = false;
      if (wrap) wrap.classList.remove('is-loading');
    }
  });

  /* -----------------------------------------------------------------------
   * Product gallery: thumbs (desktop) + scroll-snap carousel (mobile)
   * --------------------------------------------------------------------- */
  function initProductGallery(gallery) {
    if (gallery.dataset.galleryInit === 'true') return;
    gallery.dataset.galleryInit = 'true';
    const viewport = gallery.querySelector('[data-gallery-viewport]');
    const items = $$('.product-gallery__item', gallery);
    const dots = $$('.product-gallery__dot', gallery);
    const thumbs = $$('.product-gallery__thumb', gallery);
    const mobileQuery = window.matchMedia('(max-width: 749px)');

    // Note: non-active items are hidden with display:none on desktop (CSS),
    // which already removes them from the accessibility tree. No aria-hidden
    // here — on mobile every slide is visible and must stay reachable.
    const setActive = (id) => {
      items.forEach(item => item.classList.toggle('is-active', item.dataset.mediaId === id));
      thumbs.forEach(t => t.classList.toggle('is-active', t.dataset.thumbId === id));
      dots.forEach(d => d.classList.toggle('is-active', d.dataset.thumbId === id));
    };

    [...thumbs, ...dots].forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.thumbId;
        if (mobileQuery.matches && viewport) {
          const target = items.find(it => it.dataset.mediaId === id);
          if (target) viewport.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        }
        setActive(id);
      });
    });

    if (viewport) {
      let scrollTimer;
      viewport.addEventListener('scroll', () => {
        if (!mobileQuery.matches) return;
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const center = viewport.scrollLeft + viewport.clientWidth / 2;
          let closest = null;
          let minDist = Infinity;
          items.forEach(item => {
            const itemCenter = item.offsetLeft + item.clientWidth / 2;
            const dist = Math.abs(center - itemCenter);
            if (dist < minDist) { minDist = dist; closest = item; }
          });
          if (closest) setActive(closest.dataset.mediaId);
        }, 90);
      }, { passive: true });
    }
  }
  $$('[data-product-gallery]').forEach(initProductGallery);

  // Theme editor re-renders a section's DOM, dropping per-element listeners —
  // re-init anything stateful inside the reloaded section.
  document.addEventListener('shopify:section:load', (e) => {
    $$('[data-product-gallery]', e.target).forEach(initProductGallery);
    $$('video[data-lazy-video]', e.target).forEach(v => observeLazyVideo(v));
  });

  /* -----------------------------------------------------------------------
   * Collection sort
   * --------------------------------------------------------------------- */
  $$('[data-collection-sort] select').forEach(select => {
    select.addEventListener('change', (e) => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.target.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    });
  });

  /* -----------------------------------------------------------------------
   * Sticky mobile add-to-cart
   * --------------------------------------------------------------------- */
  (function initStickyAtc() {
    const sticky = $('[data-sticky-atc]');
    const buyButtons = $('[data-buy-buttons]');
    if (!sticky || !buyButtons) return;

    let hideTimer = null;
    const show = () => {
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
      sticky.hidden = false;
      requestAnimationFrame(() => {
        sticky.classList.add('is-visible');
        sticky.setAttribute('aria-hidden', 'false');
      });
    };
    const hide = () => {
      sticky.classList.remove('is-visible');
      sticky.setAttribute('aria-hidden', 'true');
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        sticky.hidden = true;
        hideTimer = null;
      }, 250);
    };

    let buyButtonsSeen = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          buyButtonsSeen = true;
          hide();
        } else if (buyButtonsSeen && entry.boundingClientRect.top < 0) {
          show();
        } else {
          hide();
        }
      },
      { rootMargin: '0px 0px -120px 0px' }
    );
    io.observe(buyButtons);

    const stickyBtn = $('[data-sticky-atc-button]', sticky);
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        const form = document.querySelector('form.product__form');
        if (form) form.requestSubmit();
      });
    }
  })();

  /* -----------------------------------------------------------------------
   * Product card quick-add (single-variant products)
   * --------------------------------------------------------------------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData
      });
      const data = await res.json();
      if (data.status && data.status >= 400) {
        throw new Error(data.description || 'Unable to add');
      }
      const cart = await fetchCart();
      updateCartCount(cart.item_count);
      btn.textContent = 'Added';
      if (cartDrawer && cartTypeIsDrawer) {
        await refreshCartDrawer();
        openCartDrawer();
      }
    } catch (err) {
      // Surface the real reason where we can — "Try again" on a sold-out
      // item is a promise that can never be kept.
      btn.textContent = /sold out/i.test(err && err.message) ? 'Sold out' : 'Try again';
    } finally {
      setTimeout(() => {
        btn.textContent = originalLabel;
        btn.disabled = false;
      }, 1400);
    }
  });

  /* -----------------------------------------------------------------------
   * Lazy autoplay videos — don't pull multi-MB files until near the viewport
   * --------------------------------------------------------------------- */
  const loadLazyVideo = (video) => {
    $$('source[data-src]', video).forEach(source => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.load();
    const play = video.play();
    if (play && play.catch) play.catch(() => {});
  };

  const lazyVideoObserver = ('IntersectionObserver' in window)
    ? new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          lazyVideoObserver.unobserve(entry.target);
          loadLazyVideo(entry.target);
        });
      }, { rootMargin: '300px 0px' })
    : null;

  function observeLazyVideo(video) {
    if (video.dataset.lazyVideoInit === 'true') return;
    video.dataset.lazyVideoInit = 'true';
    if (lazyVideoObserver) lazyVideoObserver.observe(video);
    else loadLazyVideo(video);
  }
  $$('video[data-lazy-video]').forEach(observeLazyVideo);

  /* -----------------------------------------------------------------------
   * Product recommendations
   * --------------------------------------------------------------------- */
  $$('[data-product-recommendations]').forEach(async (el) => {
    const url = el.dataset.url;
    if (!url) return;
    try {
      const res = await fetch(url);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = doc.querySelector('[data-product-recommendations]');
      if (incoming) el.innerHTML = incoming.innerHTML;
    } catch (err) {
      // Fail silently — recommendations are non-critical.
    }
  });

  /* -----------------------------------------------------------------------
   * Escape closes drawers
   * --------------------------------------------------------------------- */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    [cartDrawer, mobileNav, searchDrawer].forEach(d => {
      if (d && d.classList.contains('is-open')) closeDrawer(d);
    });
    if (collectionFilters && collectionFilters.filters.classList.contains('is-open')) {
      collectionFilters.close();
    }
    if (searchSuggest) searchSuggest.clear();
    if (shopTheLook) shopTheLook.closeAll();
  });

  /* -----------------------------------------------------------------------
   * Sticky header shrink on scroll
   * --------------------------------------------------------------------- */
  const header = $('[data-site-header]');
  if (header && header.classList.contains('site-header--sticky')) {
    let last = 0;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      header.classList.toggle('site-header--scrolled', y > 12);
      last = y;
    }, { passive: true });
  }
})();
